import {
  ArrowTrendingUpIcon,
  BookOpenIcon,
  CalendarIcon,
  ChartBarSquareIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  GiftIcon,
  IdentificationIcon,
  MegaphoneIcon,
  ShoppingBagIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useRef } from 'react';

import { ClientOnly } from '@/components/ClientOnly';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Logo } from '@/components/Logo';

import UserDropdown from '@/components/Nav/UserDropdown';
import useMe from './utils/useMe';
import { usePageContext } from './utils/usePageContext';
import { useRasContext } from './utils/useRasContext';

import NavLink from '@/components/Nav/NavLink';
import type { Link } from '@/components/Nav/NavLink';

export default Nav;

const HIDE_ON = [
  '/',
  '/signin',
  '/register',
  '/forgot-password',
  '/links',
  '/mailing-list',
  '/who-is-ras-for',
];

const LINKS: Link[] = [
  {
    count: { dataPath: '/release-center', dataName: 'messageReceipt/created' },
    href: '/release-center',
    icon: MegaphoneIcon,
    text: 'Release Center',
  },
  {
    href: '/results',
    startsWithHref: '/results',
    icon: ArrowTrendingUpIcon,
    text: 'My Results',
    roleRequired: true,
  },
  {
    href: '/archive',
    startsWithHref: '/archive',
    icon: ChartBarSquareIcon,
    text: 'All Results',
  },
  {
    count: {
      dataPath: '/support-chat',
      dataName: 'directMessageReceipt/created',
      dataCb: (_event, json, setCount, map, me) => {
        if (me?.role === 'admin' || me?.role === 'staff') {
          // I'm an admin receiving a DM notification
          if (
            (json.data.user?.role === 'admin' || json.data.user?.role === 'staff') &&
            json.data.receiverUserId
          ) {
            // When an admin messages a user, we clear all the notifications for this chat for everyone

            const path = `/support-chat/${json.data.receiverUserId}`;
            const pathCount = map.current.get(path);

            if (pathCount) {
              setCount((count) => Math.max(count - pathCount, 0));
              map.current.delete(path);
            }
          } else {
            // When a regular user messages admins, we increment the count if we aren't on that page already
            const path = `/support-chat/${json.data.userId}`;
            const pathCount = map.current.get(path);

            // 1647 is Cleatus
            if (!pathCount && location.pathname !== path && path !== '/support-chat/1647') {
              map.current.set(path, 1);
              setCount((count) => count + 1);
            }
          }
        } else {
          // I'm a regular user receiving a DM
          const path = '/support-chat';
          const pathCount = map.current.get(path);

          if (json.data.userId === me?.id) {
            // If I got it from myself, set unread to 0
            map.current.delete(path);
            setCount(0);
          } else {
            // Otherwise
            map.current.set(path, (pathCount || 0) + 1);
            setCount((count) => count + 1);
          }
        }
      },
      dataNavCb(path, setCount, map) {
        const pathCount = map.current.get(path);

        if (pathCount) {
          setCount((count) => Math.max(count - pathCount, 0));
          map.current.delete(path);
        }
      },
    },
    href: '/support-chat',
    startsWithHref: '/support-chat',
    icon: ChatBubbleLeftRightIcon,
    text: 'Support Chat',
  } as Link<API.DirectMessageReceipt>,
  {
    href: '/order/cbb',
    startsWithHref: '/order',
    icon: ShoppingBagIcon,

    text: 'Order',
  },
  {
    href: '/release-schedule',
    icon: CalendarIcon,
    text: 'Release Schedule',
  },
  {
    href: '/welcome-guide',
    icon: BookOpenIcon,
    text: 'Welcome Guide',
  },
  {
    href: '/about',
    icon: IdentificationIcon,
    text: 'About RAS',
  },
];

type TempLink = {
  href: string;
  text: string;
};

const TEMPLINKS: TempLink[] = import.meta.env.DEV
  ? [
      {
        href: '/signin',
        text: 'Sign in',
      },
      {
        href: '/register',
        text: 'Sign Up',
      },
      {
        href: '/forgot-password',
        text: 'Forgot Password',
      },
      {
        href: '/404',
        text: '404',
      },
      {
        href: '/',
        text: 'Index',
      },
    ]
  : [];

function Nav({ withoutUser = false }) {
  const { urlParsed, urlPathname } = usePageContext();
  const ref = useRef<HTMLInputElement>(null);

  const rasContext = useRasContext();
  // biome-ignore lint/correctness/useHookAtTopLevel: This is used in the <Suspense> of Nav, so it never changes
  const me = withoutUser ? null : useMe();
  const user = me || rasContext.data?.user;

  const filteredLinks = LINKS.filter((link) => {
    if (link.roleRequired && !user?.role) {
      return false;
    }

    return true;
  });

  if (HIDE_ON.includes(urlPathname)) {
    return null;
  }

  return (
    <>
      <div className="-translate-x-full peer-checked/nav-checkbox:-translate-x-0 peer-focus-within/nav:-translate-x-0 fixed inset-0 z-20 bg-zinc-400/20 backdrop-blur-xs transition-transform dark:bg-black/40" />
      <input
        ref={ref}
        id="nav-checkbox"
        type="checkbox"
        className="peer/nav-checkbox absolute z-[-1] m-0 size-10 border-collapse cursor-pointer border-transparent border-none bg-transparent p-0 opacity-0 md:hidden"
        defaultChecked={false}
      />
      <aside
        id="ras-sidebar"
        aria-label="Sidebar"
        className="peer/nav -translate-x-full fixed top-0 left-0 z-40 flex h-[100dvh] w-[100dvw] flex-none select-none flex-col bg-zinc-900 text-sm text-white transition-transform focus-within:translate-x-0 peer-checked/nav-checkbox:translate-x-0 md:w-72 md:translate-x-0 dark:border-white/10 dark:border-r dark:bg-zinc-950"
        onClick={(event) => {
          const target = event.target as HTMLAnchorElement;

          if (target?.closest('a[href]') && ref.current) {
            // Immediately close menu if anchor clicked
            target.blur();
            ref.current.focus();
            ref.current.click();
          }
        }}
      >
        <article className="flex grow flex-col space-y-5 overflow-y-auto overscroll-contain p-4 px-6">
          <div className="flex items-center justify-between">
            <Logo className="size-10 select-none" />
            <div className="flex flex-row items-center">
              <a href="/settings">
                <Cog6ToothIcon
                  height={24}
                  className="hidden size-6 shrink-0 text-zinc-400 hover:text-white active:text-white md:block"
                  aria-hidden="true"
                />
              </a>
              <label
                htmlFor="nav-checkbox"
                className="ml-2 text-zinc-400 hover:text-white peer-checked/nav-checkbox:inline-block peer-focus-within/nav:inline-block md:pointer-events-none md:hidden md:opacity-0"
              >
                <XMarkIcon height={24} className="size-6 shrink-0" aria-hidden="true" />
              </label>
            </div>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col space-y-7">
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  {filteredLinks.map((link) => {
                    return (
                      <NavLink key={link.href + link.text} link={link} urlParsed={urlParsed} />
                    );
                  })}
                </ul>
              </li>
              {/* <div className="rounded-sm bg-emerald-400/20 px-4 py-2">
                <p className="font-semibold text-white text-xs">Betting Talk</p>
                <p className="mt-1 pr-1 text-xs">
                  A premium community with exclusive content and access to 60+ RAS releases per
                  year.
                </p>
                <a href="/bettingtalk" className="text-emerald-400 text-xs">
                  Learn more
                </a>
              </div> */}
              {/* Temporary Links */}
              <li>
                <ul role="list" className="-mx-2 space-y-1">
                  <ErrorBoundary>
                    <ClientOnly component={() => import('@/components/Nav/Admin')} />
                  </ErrorBoundary>
                  {TEMPLINKS.map((link) => {
                    return (
                      <li key={link.href + link.text}>
                        <a
                          href={link.href}
                          data-active={urlParsed.pathnameOriginal === link.href}
                          className="flex w-full items-center rounded-sm p-2 text-zinc-400 transition-[width,color,background-color] duration-500 ease-out hover:bg-white/5 hover:text-white hover:duration-75 active:bg-white/5 active:text-white data-[active=true]:bg-white/10 data-[active=true]:text-white"
                        >
                          <span className="ml-3 flex-1 whitespace-nowrap">{link.text}</span>
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </li>
              <li>
                <ErrorBoundary>
                  <ClientOnly component={() => import('@/components/Nav/ActiveServices')} />
                </ErrorBoundary>
              </li>
            </ul>
          </nav>
        </article>
        <article className="mt-auto flex grow-0 flex-col space-y-1 p-2">
          <NavLink
            link={{ href: '/betonline', icon: GiftIcon, text: 'Get $250 credit with BetOnline' }}
            color="red"
            urlParsed={urlParsed}
          />
          <NavLink
            link={{ href: '/bet105', icon: GiftIcon, text: 'Get $250 credit with bet105' }}
            color="secondary"
            urlParsed={urlParsed}
          />
          <NavLink
            link={{ href: '/oddslogic', icon: GiftIcon, text: 'Get $250 credit with OddsLogic' }}
            color="primary"
            urlParsed={urlParsed}
          />
          <hr className="h-px border-0 bg-white/10" />
          <div className="flex grow justify-between gap-x-1.5">
            <ErrorBoundary>
              <ClientOnly
                component={() => import('@/components/Nav/UserDropdown')}
                key="client-user"
              >
                <UserDropdown withoutUser={true} user={user} key="server-user" />
              </ClientOnly>
              <ClientOnly component={() => import('@/components/UI/DesktopNotifications')} />
            </ErrorBoundary>
          </div>
        </article>
      </aside>
    </>
  );
}
